import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';

export type MaintenanceLevel = 'light' | 'heavy';

interface AppSettings {
  maintenance_mode: boolean;
  maintenance_level: MaintenanceLevel;
  maintenance_message: string;
  maintenance_title: string;
  updated_at: string;
  updated_by: string | null;
}

const defaultSettings: AppSettings = {
  maintenance_mode: false,
  maintenance_level: 'light',
  maintenance_message: 'Aplikasi sedang dalam perbaikan. Silakan coba lagi nanti.',
  maintenance_title: 'Maintenance Mode',
  updated_at: new Date().toISOString(),
  updated_by: null
};

export const useAppSettings = () => {
  const [settings, setSettings] = useState<AppSettings>(defaultSettings);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // First try authenticated direct query
      const { data, error: fetchError } = await supabase
        .from('app_settings')
        .select('*')
        .eq('id', 'main')
        .single();
      
      if (fetchError) {
        if (fetchError.code === 'PGRST301' || fetchError.message?.includes('JWT')) {
          // Not authenticated - try edge function
          try {
            const { data: publicData } = await supabase.functions.invoke('get-public-settings');
            if (publicData) {
              const s = {
                maintenance_mode: publicData.maintenance_mode ?? false,
                maintenance_level: (publicData.maintenance_level as MaintenanceLevel) ?? 'light',
                maintenance_message: publicData.maintenance_message ?? defaultSettings.maintenance_message,
                maintenance_title: publicData.maintenance_title ?? defaultSettings.maintenance_title,
                updated_at: new Date().toISOString(),
                updated_by: null
              };
              setSettings(s);
              localStorage.setItem('cachedAppSettings', JSON.stringify(s));
              return;
            }
          } catch {}
        }
        if (fetchError.code === 'PGRST116') {
          // No row found - use defaults
          setSettings(defaultSettings);
        } else {
          // Query failed (timeout etc) - use cached
          const cached = localStorage.getItem('cachedAppSettings');
          if (cached) {
            try { setSettings(JSON.parse(cached)); } catch {}
          } else {
            setSettings(defaultSettings);
          }
        }
      } else if (data) {
        const s = {
          maintenance_mode: data.maintenance_mode ?? false,
          maintenance_level: (data.maintenance_level as MaintenanceLevel) ?? 'light',
          maintenance_message: data.maintenance_message ?? defaultSettings.maintenance_message,
          maintenance_title: data.maintenance_title ?? defaultSettings.maintenance_title,
          updated_at: data.updated_at ?? new Date().toISOString(),
          updated_by: data.updated_by
        };
        setSettings(s);
        localStorage.setItem('cachedAppSettings', JSON.stringify(s));
      }
    } catch (err) {
      console.error('Error fetching app settings:', err);
      // Use cached on any error
      const cached = localStorage.getItem('cachedAppSettings');
      if (cached) {
        try { setSettings(JSON.parse(cached)); } catch { setSettings(defaultSettings); }
      } else {
        setSettings(defaultSettings);
      }
    } finally {
      setLoading(false);
    }
  };

  const updateSettings = async (updates: Partial<AppSettings>) => {
    try {
      setError(null);
      
      const { error: updateError } = await supabase
        .from('app_settings')
        .update({
          ...updates,
          updated_at: new Date().toISOString()
        })
        .eq('id', 'main');
      
      if (updateError) throw updateError;
      
      // Refetch to get updated data
      await fetchSettings();
      return { success: true };
    } catch (err) {
      console.error('Error updating app settings:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to update settings';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  useEffect(() => {
    fetchSettings();

    // Subscribe to realtime updates
    const channelId = `app_settings_changes_${Math.random().toString(36).substring(7)}`;
    const channel = supabase
      .channel(channelId)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'app_settings',
          filter: 'id=eq.main'
        },
        (payload) => {
          console.log('App settings changed:', payload);
          if (payload.new) {
            const newData = payload.new as Record<string, unknown>;
            setSettings({
              maintenance_mode: (newData.maintenance_mode as boolean) ?? false,
              maintenance_level: (newData.maintenance_level as MaintenanceLevel) ?? 'light',
              maintenance_message: (newData.maintenance_message as string) ?? defaultSettings.maintenance_message,
              maintenance_title: (newData.maintenance_title as string) ?? defaultSettings.maintenance_title,
              updated_at: (newData.updated_at as string) ?? new Date().toISOString(),
              updated_by: newData.updated_by as string | null
            });
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  return {
    settings,
    loading,
    error,
    updateSettings,
    refetch: fetchSettings
  };
};
